import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShortTextIcon from '@mui/icons-material/ShortText';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useQueryClient } from '@tanstack/react-query';
import { useGetReportById, useRecommendationsHistory } from '../../hooks/reports';
import { useTestConfigurations, useUserPrompts } from '../../hooks/prompts';
import reportsService from '../../api/reportsService';
import authService from '../../api/authService';
import CanvasGridDashboard from '../CanvasGridDashboard';
import type { RecommendationHistoryEntry, Reports } from '../../types/types';
import { toast } from 'react-toastify';
import { t } from '../../i18n/t';
import {
    downloadReportPdf,
    downloadReportXls,
    getErrorMessage,
} from '../../utils/reportDownloads';

type ChartPoint = {
    x: number;
    y: number;
    y1?: number;
    isRPeaks: boolean;
    accelX?: number;
    accelY?: number;
    accelZ?: number;
    marker?: unknown;
    sdnn?: unknown;
    rmssd?: unknown;
    pnn20?: unknown;
    pnn50?: unknown;
};

const scale = (points: ChartPoint[]): ChartPoint[] => {
    const minValValue = Math.max(0, Math.min(...points.map((obj) => obj.y)));
    const maxValValue = Math.min(300, Math.max(...points.map((obj) => obj.y)));
    const nonZeroPhotos = points.filter((obj) => obj.y1 !== 0).map((obj) => obj.y1 as number);
    const minValPhoto = nonZeroPhotos.length ? Math.min(...nonZeroPhotos) : 0;
    const maxValPhoto = nonZeroPhotos.length ? Math.max(...nonZeroPhotos) : 0;
    const nonZeroAccelX = points.filter((obj) => obj.accelX !== 0).map((obj) => obj.accelX as number);
    const minValAccelX = nonZeroAccelX.length ? Math.min(...nonZeroAccelX) : 0;
    const maxValAccelX = nonZeroAccelX.length ? Math.max(...nonZeroAccelX) : 0;
    const nonZeroAccelY = points.filter((obj) => obj.accelY !== 0).map((obj) => obj.accelY as number);
    const minValAccelY = nonZeroAccelY.length ? Math.min(...nonZeroAccelY) : 0;
    const maxValAccelY = nonZeroAccelY.length ? Math.max(...nonZeroAccelY) : 0;
    const nonZeroAccelZ = points.filter((obj) => obj.accelZ !== 0).map((obj) => obj.accelZ as number);
    const minValAccelZ = nonZeroAccelZ.length ? Math.min(...nonZeroAccelZ) : 0;
    const maxValAccelZ = nonZeroAccelZ.length ? Math.max(...nonZeroAccelZ) : 0;
    return points.map((obj) => ({
        ...obj,
        y: obj.y,
        y1: obj.y1 === 0 || obj.y1 == null ? obj.y1 : (maxValPhoto === minValPhoto ? maxValValue : ((obj.y1 - minValPhoto) / (maxValPhoto - minValPhoto)) * (maxValValue - minValValue)),
        accelX: obj.accelX === 0 || obj.accelX == null ? obj.accelX : (maxValAccelX === minValAccelX ? maxValValue : ((obj.accelX - minValAccelX) / (maxValAccelX - minValAccelX)) * (maxValValue - minValValue)),
        accelY: obj.accelY === 0 || obj.accelY == null ? obj.accelY : (maxValAccelY === minValAccelY ? maxValValue : ((obj.accelY - minValAccelY) / (maxValAccelY - minValAccelY)) * (maxValValue - minValValue)),
        accelZ: obj.accelZ === 0 || obj.accelZ == null ? obj.accelZ : (maxValAccelZ === minValAccelZ ? maxValValue : ((obj.accelZ - minValAccelZ) / (maxValAccelZ - minValAccelZ)) * (maxValValue - minValValue)),
    }));
};

const formatDate = (dateString: string | null | undefined) => {
    if (dateString == null) return '';
    const date = new Date(dateString);
    return date.toLocaleString(navigator.language, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};

const DashboardReportDetail = () => {
    const { userId, reportId } = useParams<{ userId: string; reportId: string }>();
    const navigate = useNavigate();
    const uid = userId ? Number(userId) : undefined;
    const rid = reportId ? Number(reportId) : undefined;

    const queryClient = useQueryClient();
    const { data: report, isLoading: reportsLoading } = useGetReportById(rid, uid);

    const [user, setUser] = useState<{ id?: number; first_name?: string; last_name?: string; middle_name?: string } | null>(null);
    const [allPoints, setAllPoints] = useState<ChartPoint[]>([{ x: 0, y: 0, y1: 0, isRPeaks: false }]);
    const [pointsLoading, setPointsLoading] = useState(false);
    const [loadingXls, setLoadingXls] = useState(false);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [loadingShortPdf, setLoadingShortPdf] = useState(false);
    const [withFilter, setWithFilter] = useState(false);
    const [selectedPromptId, setSelectedPromptId] = useState<number | ''>('');
    const [userQuestion, setUserQuestion] = useState('');
    const [currentResult, setCurrentResult] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [reportProperties, setReportProperties] = useState<Array<{
        id?: number;
        name: string;
        value: string;
        description?: string | null;
        timestamp?: number | string | null;
        created_at?: string;
    }>>([]);
    const [propertiesPage, setPropertiesPage] = useState(0);
    const [propertiesPageSize, setPropertiesPageSize] = useState(10);

    const { data: prompts = [] } = useUserPrompts(uid);
    const { data: testConfigs = [] } = useTestConfigurations();
    const { data: history = [], refetch: refetchHistory } = useRecommendationsHistory(rid ?? undefined, uid ?? undefined);
    const configById = useMemo(() => {
        const m: Record<number, string> = {};
        testConfigs.forEach((c) => { m[c.id] = c.name; });
        return m;
    }, [testConfigs]);

    const [canSeeUsersNav, setCanSeeUsersNav] = useState(false);

    useEffect(() => {
        authService
            .getAccessContext()
            .then((res) =>
                setCanSeeUsersNav(!!res?.data?.isAdmin || !!res?.data?.hasSubordinates),
            )
            .catch(() => setCanSeeUsersNav(false));
    }, []);

    useEffect(() => {
        if (rid == null || !Number.isFinite(rid)) {
            setReportProperties([]);
            setPropertiesPage(0);
            return;
        }
        reportsService
            .getReportProperties(rid)
            .then((data) => {
                setReportProperties(data?.properties ?? []);
                setPropertiesPage(0);
            })
            .catch(() => {
                setReportProperties([]);
                setPropertiesPage(0);
            });
    }, [rid]);

    const pagedProperties = useMemo(() => {
        const start = propertiesPage * propertiesPageSize;
        return reportProperties.slice(start, start + propertiesPageSize);
    }, [reportProperties, propertiesPage, propertiesPageSize]);

    useEffect(() => {
        if (uid) {
            authService.getUserById(uid).then((res) => setUser(res?.data ?? null)).catch(() => setUser(null));
        }
    }, [uid]);

    useEffect(() => {
        if (!report?.value) return;
        setPointsLoading(true);
        const data = report.pin
            ? { value: report.value, pin: report.pin, withFilter }
            : { value: report.value, withFilter };
        reportsService
            .getPoints(data)
            .then((response: { data?: { points?: Array<Record<string, unknown>> } }) => {
                const points: ChartPoint[] = (response.data?.points ?? []).map((el: Record<string, unknown>, idx: number) => {
                    const photo = el.photo;
                    const accel = el.accel as number[] | undefined;
                    if (!el.accel && !el.accelX) {
                        return {
                            x: idx,
                            y: el.value as number,
                            y1: photo as number,
                            isRPeaks: false,
                            marker: el.marker,
                            sdnn: el.sdnn,
                            rmssd: el.rmssd,
                            pnn20: el.pnn20,
                            pnn50: el.pnn50,
                        };
                    }
                    return {
                        x: idx,
                        y: el.value as number,
                        y1: photo as number,
                        accelX: (el.accelX as number) || accel?.[0] || 0,
                        accelY: (el.accelY as number) || accel?.[1] || 0,
                        accelZ: (el.accelZ as number) || accel?.[2] || 0,
                        isRPeaks: false,
                        marker: el.marker,
                        sdnn: el.sdnn,
                        rmssd: el.rmssd,
                        pnn20: el.pnn20,
                        pnn50: el.pnn50,
                    };
                });
                setAllPoints(scale(points));
            })
            .catch(() => setAllPoints([{ x: 0, y: 0, y1: 0, isRPeaks: false }]))
            .finally(() => setPointsLoading(false));
    }, [report?.value, report?.pin, withFilter]);

    const handleDelete = () => {
        if (!report || !window.confirm(t('reportDetail.deleteReportConfirm'))) return;
        reportsService.delete({ value: report.value, pin: report.pin })
            .then(() => {
                if (uid != null) queryClient.invalidateQueries({ queryKey: ['reportsAdmin', uid] });
                queryClient.invalidateQueries({ queryKey: ['reports'] });
                navigate(`/dashboard/users/${userId}`);
            })
            .catch((err) => {
                toast.error(getErrorMessage(err, t('reportDetail.deleteFailed')));
            });
    };

    const handleXls = async () => {
        if (!report) return;
        setLoadingXls(true);
        try {
            await downloadReportXls(report, user, withFilter);
        } catch (err) {
            toast.error(getErrorMessage(err, t('reportDetail.xlsFailed')));
        } finally {
            setLoadingXls(false);
        }
    };

    const downloadPdf = async (short: boolean) => {
        if (!report || !uid) return;
        if (short) setLoadingShortPdf(true);
        else setLoadingPdf(true);
        try {
            await downloadReportPdf(report, uid, short);
        } catch (err) {
            toast.error(getErrorMessage(err, short ? t('reportDetail.shortPdfFailed') : t('reportDetail.pdfFailed')));
        } finally {
            if (short) setLoadingShortPdf(false);
            else setLoadingPdf(false);
        }
    };

    const [currentQuestion, setCurrentQuestion] = useState<string>('');
    const [currentRequestPayload, setCurrentRequestPayload] = useState<string>('');

    const handleGenerate = async () => {
        if (uid == null || rid == null) return;
        const promptId = selectedPromptId === '' ? undefined : selectedPromptId;
        if (promptId == null) {
            toast.error(t('reportDetail.selectPrompt'));
            return;
        }
        setIsGenerating(true);
        setCurrentResult('');
        setCurrentRequestPayload('');
        try {
            const trimmedQuestion = userQuestion.trim();
            const result = await authService.generateRecommendationsByReportId({
                userId: uid,
                reportId: rid,
                promptId,
                userQuestion: trimmedQuestion || undefined,
            });
            if (result.data.success && result.data.recommendations != null) {
                const data = result.data as { recommendations: string; requestPayload?: string };
                setCurrentResult(data.recommendations);
                setCurrentQuestion(trimmedQuestion || '');
                setCurrentRequestPayload(data.requestPayload ?? '');
                refetchHistory();
            } else {
                toast.error((result.data as { error?: string }).error ?? t('reportDetail.genError'));
            }
        } catch (err) {
            toast.error(getErrorMessage(err, t('reportDetail.genFailed')));
        } finally {
            setIsGenerating(false);
        }
    };

    const formatHistoryQuestion = (q: string | undefined) => {
        if (q == null || q === '') return '—';
        if (q.startsWith('__gen_')) return '—';
        return q.length > 60 ? q.slice(0, 60) + '…' : q;
    };

    const formatHistoryRequest = (r: string | undefined) => {
        if (!r) return '';
        const s = r.replace(/\s+/g, ' ').trim();
        return s.length > 80 ? s.slice(0, 80) + '…' : s;
    };

    if (reportsLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
            </Box>
        );
    }

    if (!report) {
        return (
            <Box p={2}>
                <Typography color="text.secondary">{t('reportDetail.notFound')}</Typography>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                        if (canSeeUsersNav) {
                            navigate(uid ? `/dashboard/users/${uid}` : '/dashboard/users');
                        } else {
                            navigate('/dashboard');
                        }
                    }}
                    sx={{ mt: 2 }}
                >
                    {t('reportDetail.backToUser')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                    if (canSeeUsersNav) {
                        navigate(`/dashboard/users/${userId}`);
                    } else {
                        navigate('/dashboard');
                    }
                }}
                sx={{ mb: 2 }}
            >
                {t('reportDetail.backToUser')}
            </Button>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('reportDetail.reportN', { id: report.id })}
                </Typography>
                <Typography variant="body2">{t('reportDetail.date')} {formatDate((report as Reports & { created_at?: string }).created_at)}</Typography>
                {(report as Reports & { time?: string }).time != null && (
                    <Typography variant="body2">Time: {(report as Reports & { time?: string }).time}</Typography>
                )}
                {(report as Reports & { comment?: string }).comment != null && (
                    <Typography variant="body2">{t('reportDetail.comment')} {(report as Reports & { comment?: string }).comment}</Typography>
                )}
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    value: {(report.value || '').slice(0, 24)}… | pin: {(report.pin || '').slice(0, 12)}…
                </Typography>
                {(report.testConfiguration?.name ||
                    report.testConfigurationId != null) && (
                    <>
                        <Typography variant="body2">
                            {t('reportDetail.testConfig')}{' '}
                            {report.testConfiguration?.name?.trim() ||
                                (report.testConfigurationId != null
                                    ? `#${report.testConfigurationId}`
                                    : '—')}
                            {report.testConfiguration?.id != null &&
                                report.testConfiguration?.name?.trim() &&
                                ` (id: ${report.testConfiguration.id})`}
                        </Typography>
                        {report.testConfiguration?.categoryName?.trim() && (
                            <Typography variant="body2" color="text.secondary">
                                {t('reportDetail.testConfigCategory')}{' '}
                                {report.testConfiguration.categoryName}
                            </Typography>
                        )}
                    </>
                )}
                {user && (
                    <Typography variant="body2" color="text.secondary">
                        {t('reportDetail.user')} {user.last_name} {user.first_name} {user.middle_name} (id: {user.id})
                    </Typography>
                )}
            </Paper>

            {reportProperties.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('reportDetail.properties')}
                    </Typography>
                    <TableContainer sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('reportDetail.propertiesTime')}</TableCell>
                                    <TableCell>{t('reportDetail.propertiesName')}</TableCell>
                                    <TableCell>{t('reportDetail.propertiesValue')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedProperties.map((p, idx) => {
                                    const ts = p.timestamp != null && p.timestamp !== ''
                                        ? Number(p.timestamp)*1000
                                        : (p.created_at ? new Date(p.created_at).getTime() : NaN);
                                    debugger
                                    return (
                                        <TableRow key={p.id ?? `${propertiesPage}-${idx}`}>
                                            <TableCell>
                                                {Number.isFinite(ts) ? formatDate(new Date(ts).toISOString()) : '—'}
                                            </TableCell>
                                            <TableCell>{p.name}</TableCell>
                                            <TableCell>{p.value}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={reportProperties.length}
                        page={propertiesPage}
                        onPageChange={(_e, newPage) => setPropertiesPage(newPage)}
                        rowsPerPage={propertiesPageSize}
                        onRowsPerPageChange={(e) => {
                            setPropertiesPageSize(parseInt(e.target.value, 10));
                            setPropertiesPage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50]}
                    />
                </Paper>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={withFilter}
                            onChange={(_, checked) => setWithFilter(checked)}
                            color="primary"
                        />
                    }
                    label={t('reportDetail.ecgFilter')}
                />
                <Button variant="outlined" startIcon={loadingXls ? <CircularProgress size={20} /> : <DescriptionIcon />} onClick={handleXls} disabled={loadingXls}>
                    XLS
                </Button>
                <Button variant="outlined" startIcon={loadingPdf ? <CircularProgress size={20} /> : <PictureAsPdfIcon />} onClick={() => downloadPdf(false)} disabled={loadingPdf}>
                    PDF
                </Button>
                <Button variant="outlined" startIcon={loadingShortPdf ? <CircularProgress size={20} /> : <ShortTextIcon />} onClick={() => downloadPdf(true)} disabled={loadingShortPdf}>
                    {t('reportDetail.shortPdf')}
                </Button>
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
                    {t('common.delete')}
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('reportDetail.aiGen')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2, minWidth: 0 }}>
                    <Select
                        size="small"
                        displayEmpty
                        value={selectedPromptId}
                        onChange={(e) => setSelectedPromptId(e.target.value === '' ? '' : Number(e.target.value))}
                        sx={{ minWidth: 200, maxWidth: '100%' }}
                        renderValue={(v: number | string) => {
                            if (v === '') return t('reportDetail.selectPrompt');
                            const id = typeof v === 'string' ? Number(v) : v;
                            const p = prompts.find((x) => x.id === id);
                            return p ? `${p.name} (${configById[p.testConfigurationId] ?? p.testConfigurationId})` : String(v);
                        }}
                    >
                        <MenuItem value="">{t('reportDetail.selectPrompt')}</MenuItem>
                        {prompts.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.name} — {configById[p.testConfigurationId] ?? p.testConfigurationId}
                            </MenuItem>
                        ))}
                    </Select>
                    <TextField
                        size="small"
                        placeholder={t('reportDetail.optionalQuestion')}
                        value={userQuestion}
                        onChange={(e) => setUserQuestion(e.target.value)}
                        sx={{ flex: 1, minWidth: 0 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={isGenerating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                        onClick={handleGenerate}
                        disabled={isGenerating}
                    >
                        {isGenerating ? t('reportDetail.generating') : t('reportDetail.generate')}
                    </Button>
                </Box>
                {currentResult && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('reportDetail.result')}
                        </Typography>
                        <Stack spacing={2}>
                            {currentQuestion.trim() !== '' && (
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: 1,
                                        bgcolor: 'action.hover',
                                        borderLeft: 3,
                                        borderColor: 'info.main',
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        {t('reportDetail.clarifyingQuestionLabel')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                        {currentQuestion}
                                    </Typography>
                                </Box>
                            )}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    bgcolor: 'grey.100',
                                    borderLeft: 3,
                                    borderLeftColor: 'divider',
                                    maxHeight: 240,
                                    overflow: 'auto',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                    {t('reportDetail.inputSent')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {currentRequestPayload || '—'}
                                </Typography>
                            </Paper>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderLeft: 3,
                                    borderLeftColor: 'primary.main',
                                    maxHeight: 280,
                                    overflow: 'auto',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                <Typography variant="subtitle2" gutterBottom color="primary">
                                    {t('reportDetail.answer')}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                    {currentResult}
                                </Typography>
                            </Paper>
                        </Stack>
                    </Box>
                )}
                <Typography variant="subtitle2" gutterBottom>{t('reportDetail.history')}</Typography>
                {history.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">{t('reportDetail.noHistory')}</Typography>
                ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 220, overflow: 'auto' }}>
                        {history.map((entry: RecommendationHistoryEntry) => (
                            <li
                                key={entry.id}
                                style={{ marginBottom: 8, cursor: 'pointer' }}
                                onClick={() => {
                                    setCurrentResult(entry.assistantAnswer);
                                    setCurrentQuestion(
                                        entry.userQuestion && !entry.userQuestion.startsWith('__gen_')
                                            ? entry.userQuestion
                                            : '',
                                    );
                                    setCurrentRequestPayload(entry.requestPayload ?? '');
                                }}
                            >
                                <Typography variant="body2">
                                    {formatDate(entry.createdAt)} — {formatHistoryQuestion(entry.userQuestion)}
                                </Typography>
                                {entry.requestPayload && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {t('reportDetail.inputHistory')}&nbsp;
                                        {formatHistoryRequest(entry.requestPayload)}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {t('reportDetail.answerHistory')}&nbsp;
                                    {(entry.assistantAnswer || '').slice(0, 80)}
                                    {(entry.assistantAnswer?.length ?? 0) > 80 ? '…' : ''}
                                </Typography>
                            </li>
                        ))}
                    </Box>
                )}
            </Paper>

            {pointsLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                allPoints.length > 1 && (
                    <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                        <CanvasGridDashboard points={allPoints} setPulsUser={() => {}} />
                    </Box>
                )
            )}
        </Box>
    );
};

export default DashboardReportDetail;
